/* eslint-disable no-console */
import type { FastifyReply, FastifyRequest } from 'fastify'
import { createRateLimiter as createCore } from '../../core/RateLimiter'
import type { RateShieldContext } from '../../context'
import type {
  RateLimitInfo,
  RateLimitOptions,
  RateLimitStats
} from '../../types'
import { extractIp } from './helpers'

// ── Fastify-specific instance type ─────────────────────────────────────────────

/**
 * A rate-limiter instance that can be registered directly as a Fastify
 * `onRequest` hook:
 *
 * ```ts
 * const limiter = createRateLimiter({ maxRequests: 100 })
 * app.addHook('onRequest', limiter)
 * // or on a single route:
 * app.get('/api/data', { onRequest: limiter }, handler)
 * ```
 */
export interface FastifyRateLimiterInstance {
  (request: FastifyRequest, reply: FastifyReply): Promise<void>
  destroy(): void | Promise<void>
  reset(key: string): void | Promise<void>
  resetAll(): void | Promise<void>
  getInfo(key: string): Promise<RateLimitInfo | null>
  getStats(): Promise<RateLimitStats>
}

// ── Fastify factory ────────────────────────────────────────────────────────────

/**
 * Create a rate-limiter instance that works as a Fastify `onRequest` hook.
 *
 * @example
 * ```ts
 * import Fastify from 'fastify'
 * import { createRateLimiter } from 'rate-shield/fastify'
 *
 * const app    = Fastify({ trustProxy: true })
 * const limiter = createRateLimiter({ maxRequests: 100, windowMs: 60_000 })
 *
 * app.addHook('onRequest', limiter)
 * ```
 */
export const createRateLimiter = (
  options: RateLimitOptions<FastifyRequest> = {}
): FastifyRateLimiterInstance => {
  const core = createCore<FastifyRequest>({
    keyGenerator: extractIp,
    ...options
  })

  const hook = async (
    request: FastifyRequest,
    reply: FastifyReply
  ): Promise<void> => {
    const ctx: RateShieldContext<FastifyRequest> = {
      req: request,
      setHeader: (name, value) => {
        if (!reply.sent) reply.header(name, String(value))
      },
      isResponseSent: () => reply.sent,
      block: (_statusCode, body) => {
        if (!reply.sent) void reply.status(429).send(body)
      },
      // Fastify hooks continue automatically when the async function returns
      // without sending a response — no explicit pass call needed.
      pass: () => {}
    }

    await core.handle(ctx)
  }

  return Object.assign(hook, {
    destroy: () => core.destroy(),
    reset: (key: string) => core.reset(key),
    resetAll: () => core.resetAll(),
    getInfo: (key: string) => core.getInfo(key),
    getStats: () => core.getStats()
  }) as FastifyRateLimiterInstance
}
